/*
 * Decléor — routines drawer
 * ---------------------------------------------------------------------------
 * A minimal drawer controller for the "Faites-en une routine" block, and it exists for exactly
 * one reason: the theme's own Drawer class is registered PER SECTION, and the product section does
 * not register it.
 *
 *   register('header',      [header, drawer, …])
 *   register('cart-drawer', [drawer, cartDrawer, accordion])
 *   register('product',     [productSection, accordion, tabs, swapperSection])   ← no `drawer`
 *
 * `drawer.onLoad()` only wires `this.container.querySelectorAll('[data-drawer]')`, so a drawer
 * placed inside sections/product.liquid is never initialised. theme.js is a vendored bundle we do
 * not edit, hence this file.
 *
 * It reimplements only the PLUMBING and reuses the theme's CSS and conventions verbatim, so the
 * routines drawer looks and animates exactly like the cart drawer:
 *   .drawer--visible  → the open state (all transitions live in theme.css)
 *   .display-none     → added 800ms after closing, matching the theme's own timing
 *   [data-drawer-scrolls]  → the scrollable region, and what carries the scroll lock
 *   [data-drawer-underlay] → click-to-close
 *   [data-drawer-toggle="<key>"] → any number of open/close buttons
 *   [data-first-focus]     → focused on open
 * Scroll locking goes through the theme's own `theme:scroll:lock` / `:unlock` events rather than
 * touching the body, so it cooperates with scroll-lock's scrollbar gap-fill AND with Lenis (see
 * assets/smooth-scroll.js, which mirrors `data-scroll-locked` onto lenis.stop()).
 *
 * No-op on any page without a [data-routines-drawer].
 */
(function () {
  'use strict';

  var VISIBLE = 'drawer--visible';
  var NONE = 'display-none';
  var CLOSE_MS = 800; // matches the theme's own post-close delay

  function controller(drawer) {
    var key = drawer.getAttribute('data-drawer');
    if (!key) {
      return;
    }

    /* MOVE THE DRAWER TO <body>. Not cosmetic — without it the drawer is invisible.
     * The block renders inside `.product__inner.is-sticky`, which theme.css gives a stacking
     * context at z-index 5, so a fixed overlay declared in there is trapped BELOW the header
     * (z-index 6000) however high its own z-index goes. Reparenting to <body> is the same fix
     * snippets/where-to-buy.liquid already uses for its retailers drawer.
     * The orphan sweep matters on re-render (theme editor, Section Rendering API): the previously
     * moved copy is no longer inside the section, so it survives the swap and would linger. */
    var orphan = document.querySelector('body > [data-routines-drawer][data-drawer="' + key + '"]');
    if (orphan && orphan !== drawer) {
      orphan.parentNode.removeChild(orphan);
    }
    document.body.appendChild(drawer);

    var scrolls = drawer.querySelector('[data-drawer-scrolls]') || drawer;
    var underlay = drawer.querySelector('[data-drawer-underlay]');
    var timer = null;
    var opener = null; // where focus came from, so Escape can return it

    function isOpen() {
      return drawer.classList.contains(VISIBLE);
    }

    function open(from) {
      if (isOpen()) {
        return;
      }
      opener = from || null;
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
      drawer.classList.remove(NONE);
      // A frame between removing display-none and adding the visible class, or the browser has
      // nothing to transition FROM and the drawer appears instantly.
      requestAnimationFrame(function () {
        drawer.classList.add(VISIBLE);
      });
      /* Dispatched on DOCUMENT and carrying the scrollable element as `detail` — that is the
       * contract theme.js implements: `lock(e)` calls `scrollLock.disablePageScroll(e.detail)`.
       * Firing it on the scrollable element without a detail leaves e.detail null and the lock
       * silently targets nothing. */
      document.dispatchEvent(new CustomEvent('theme:scroll:lock', { bubbles: true, detail: scrolls }));
      var first = drawer.querySelector('[data-first-focus]');
      if (first) {
        first.focus();
      }
    }

    function close() {
      if (!isOpen()) {
        return;
      }
      drawer.classList.remove(VISIBLE);
      document.dispatchEvent(new CustomEvent('theme:scroll:unlock', { bubbles: true }));
      timer = setTimeout(function () {
        drawer.classList.add(NONE);
        timer = null;
      }, CLOSE_MS);
      if (opener && document.contains(opener)) {
        opener.focus();
      }
      opener = null;
    }

    // Delegated so toggles rendered later (theme editor re-render) keep working.
    document.addEventListener('click', function (event) {
      var t = event.target.closest ? event.target.closest('[data-drawer-toggle]') : null;
      if (t && t.getAttribute('data-drawer-toggle') === key) {
        event.preventDefault();
        if (isOpen()) {
          close();
        } else {
          open(t);
        }
      }
    });

    if (underlay) {
      underlay.addEventListener('click', close);
    }

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && isOpen()) {
        close();
      }
    });
  }

  function init() {
    var drawers = document.querySelectorAll('[data-routines-drawer]');
    var i;
    for (i = 0; i < drawers.length; i += 1) {
      if (!drawers[i].dataset.rtBound) {
        drawers[i].dataset.rtBound = '1';
        controller(drawers[i]);
      }
    }
  }

  init();
  document.addEventListener('shopify:section:load', init);
})();
